import { Box } from '@material-ui/core';
import { LenderName } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { LenderIcons } from '@/components/modules/Application/components/PaymentMethod/_shared/components/LenderIcons';
import { LoanTermSelector } from '@/components/modules/Application/components/PaymentMethod/_shared/components/LoanTermSelector/LoanTermSelector';
import { PaymentHeaderWrapper } from '@/components/modules/Application/components/PaymentMethod/_shared/components/PaymentMethodCard/PaymentMethodHeader/components/PaymentHeaderWrapper';
import { PaymentMethodHeaderProps } from '@/components/modules/Application/components/PaymentMethod/_shared/components/PaymentMethodCard/types/PaymentMethodHeaderProps';
import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { AbstractPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { IconButton } from '@/components/shared/components/IconButton';
import { IconByAllowedPaymentMethodType } from '@/components/shared/components/PricingsTable/constants/IconByAllowedPaymentMethodType';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useScreen } from '@/hooks/useScreen';

export const LoanPaymentHeaderComponent = <T extends AbstractPaymentMethod>({
  paymentMethodEntity,
  isConfigurationOpen,
  toggleConfiguration,
  hasLenders,
}: PaymentMethodHeaderProps<T>) => {
  const { getTextIdTranslation } = useTranslation();
  const { sm, md, lg } = useScreen();

  const allProviderNames = useMemo(
    () => (paymentMethodEntity as unknown as AbstractLoanPaymentMethod).getAllProviderNames() as LenderName[],
    [],
  );

  const ConfigurationButton = (
    <StandardButton
      size="small"
      variant="contained"
      color="default"
      onClick={toggleConfiguration}
      style={{ minWidth: '102px' }}
      disabled={!paymentMethodEntity.isSelected}
    >
      {getTextIdTranslation(isConfigurationOpen ? 'CLOSE' : 'CONFIGURE')}
    </StandardButton>
  );

  const ConfigurationButtonSmall = (
    <IconButton
      icon={isConfigurationOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
      onClick={toggleConfiguration}
      style={{ marginLeft: md ? '-16px' : 0 }}
      disabled={!paymentMethodEntity.isSelected}
    />
  );

  const showProvidersLogos = true;

  return (
    <PaymentHeaderWrapper>
      <Icon>{IconByAllowedPaymentMethodType(paymentMethodEntity.paymentMethodType)}</Icon>
      <Box
        display="flex"
        flexDirection={'column'}
        flexGrow={1}
        alignItems={sm ? 'flex-start' : 'center'}
        flexShrink={0}
      >
        <Font variant="body2">{getTextIdTranslation(paymentMethodEntity.paymentMethodType)}</Font>
        <Box display="flex" alignItems={'center'} maxHeight={'28px'}>
          <Font variant="body1" style={{ fontWeight: 'bold' }}>
            {!paymentMethodEntity.selectedConditionsGroup ? (
              <span style={{ filter: 'blur(4px)' }}>XX,XX €</span>
            ) : (
              <>
                {getTextIdTranslation(
                  paymentMethodEntity.selectedConditionsGroup.instalmentTotalAmount().hasMultiple
                    ? 'FROM_AMOUNT'
                    : 'FOR',
                )?.toLowerCase()}{' '}
                {paymentMethodEntity.selectedConditionsGroup.instalmentTotalAmount().string}
              </>
            )}{' '}
            {getTextIdTranslation('IN')}{' '}
          </Font>
          <LoanTermSelector paymentMethodEntity={paymentMethodEntity as unknown as AbstractLoanPaymentMethod} />
        </Box>
      </Box>
      {hasLenders && showProvidersLogos && (
        <Box minWidth={0} overflow="hidden">
          <LenderIcons
            allLenderNames={allProviderNames}
            activeLenderNames={
              (paymentMethodEntity as unknown as AbstractLoanPaymentMethod).getActiveProviderNames() as LenderName[]
            }
            collapseAfter={3}
          />
        </Box>
      )}
      {!!toggleConfiguration &&
        paymentMethodEntity.areOptionsConfigurable() &&
        (lg || !sm ? ConfigurationButton : ConfigurationButtonSmall)}
    </PaymentHeaderWrapper>
  );
};
